//import logo from './logo.svg';
import React, { useState, useEffect } from 'react';
import './styles/App.css';
//import './styles/themes.css';
import Inquiry from './pages/Inquiry';
import Landing from './pages/Landing';
import Learn from './pages/Learn';
import CourseGallery from './pages/CourseGallery';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Success from './pages/Success';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Verified from './pages/Verified';
import AuthCallback from './pages/AuthCallback';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { auth } from './context/auth.ts';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import { supabase } from './integrations/client.ts';

// TODO: Setup react router

function AppContent() {
  const { user: currentUser, isAuthenticated, loading, profileLoaded, isEmailVerified, refreshProfile } = useAuth();
  const [currentView, setCurrentView] = useState(() => {
    // Check if this is the auth callback page
    if (window.location.pathname === '/auth/callback') {
      return 'authcallback';
    }
    // Check URL parameters on initial load
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    return viewParam || 'landing';
  }); // 'inquiry', 'landing', 'learn', 'signin', 'signup', 'success', 'authcallback'
  const [currentCourse, setCurrentCourse] = useState(null);
  //const [theme, setTheme] = useState('light');
  const [justSignedIn, setJustSignedIn] = useState(false); // Track if user just signed in

  // Apply theme to document on theme change
  useEffect(() => {
    //document.documentElement.setAttribute('data-theme', theme);
  }, []);

  // Handle URL parameter changes (for Stripe redirects and verified page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam && ['success', 'signup', 'signin', 'verified'].includes(viewParam)) {
      setCurrentView(viewParam);
      // Clean up URL after setting view
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Set theme based on current user's preference
  useEffect(() => {
    if (currentUser && currentUser.themePreference) {
      //setTheme(currentUser.themePreference);
    }
  }, [currentUser]);

  // Email verification gate - redirect unverified users to verification page
  useEffect(() => {
    if (isAuthenticated && currentUser && profileLoaded && !isEmailVerified() && currentView !== 'verifyemail') {
      setCurrentView('verifyemail');
    }
  }, [isAuthenticated, currentUser, profileLoaded, currentView, isEmailVerified]);

  // Handle navigation after authentication - auth listener sets justSignedIn
  useEffect(() => {
    if (isAuthenticated && currentUser && profileLoaded && isEmailVerified() && (currentView === 'signin' || currentView === 'signup')) {
      const isPremium = currentUser.subscription_type === 'premium';
      const targetView = isPremium ? 'inquiry' : 'landing';
      setCurrentView(targetView);
      setJustSignedIn(true);
    }
  }, [isAuthenticated, currentUser, profileLoaded, currentView, isEmailVerified]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    //setTheme(newTheme);

    // Update user preference if authenticated
    if (isAuthenticated && currentUser) {
      // TODO: Update theme in profiles table via Supabase
    }
  };

  // Sign out handler using Supabase
  const handleSignOut = async () => {
    const { error } = await auth.signOut();
    if (error) {
    }
    // Reset view to landing on sign out
    setCurrentView('landing');
    setCurrentCourse(null);
    // Auth state changes are handled by the AuthContext listener
  };

  const navigateToSignIn = () => {
    setCurrentView('signin');
  };

  const navigateToSignUp = () => {
    setCurrentView('signup');
  };

  const navigateToForgotPassword = () => {
    setCurrentView('forgotpassword');
  };

  const navigateToResetPassword = () => {
    setCurrentView('resetpassword');
  };

  const navigateToVerifyEmail = () => {
    setCurrentView('verifyemail');
  };

  const navigateToVerified = () => {
    setCurrentView('verified');
  };

  const navigateToInquiry = () => {
    // Only premium users can access Inquiry
    // Wait for profile to load before checking subscription
    if (isAuthenticated && profileLoaded && currentUser?.subscription_type === 'premium') {
      setCurrentView('inquiry');
    } else if (profileLoaded) {
      // Only redirect if profile has loaded and confirmed non-premium
      setCurrentView('landing');
    }
    // If profile not loaded yet, do nothing and wait
  };

  const navigateToLanding = () => {
    setCurrentView('landing');
  };

  const navigateToLearn = () => {
    setCurrentView('learn');
  };

  const navigateToCourseGallery = () => {
    setCurrentView('coursegallery');
  };

  const navigateToTermsOfUse = () => {
    setCurrentView('termsofuse');
  };

  const navigateToPrivacyPolicy = () => {
    setCurrentView('privacypolicy');
  };

  const navigateToUpgrade = async () => {
    try {
      // Get valid session (refreshes if expired)
      const { session, error: sessionError } = await auth.getValidSession();
      if (sessionError || !session) throw new Error('Authentication session expired. Please sign in again.');

      const { data: premiumTier, error: tierError } = await supabase
        .from('subscription_tiers')
        .select('stripe_price_id')
        .eq('id', 'premium')
        .single();

      if (tierError || !premiumTier?.stripe_price_id) {
        throw new Error('Premium plan is not configured. Please contact support.');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          priceId: premiumTier.stripe_price_id,
          successUrl: `${window.location.origin}/?view=success`,
          cancelUrl: `${window.location.origin}/`
        })
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to create checkout session');
      if (json.url) {
        window.location.href = json.url;
      } else {
        throw new Error('Checkout URL not returned');
      }
    } catch (err) {
      alert(err.message || 'Unable to start upgrade checkout');
    }
  };

  const handleCourseSelect = (course) => {
    setCurrentCourse(course);
    setCurrentView('learn'); // Navigate to Learn view when course is selected
  };

  // Show skeleton UI while auth is initializing
  if (loading) {
    return (
      <div className="App" style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-color, #ffffff)'
      }}>
        {/* Header Skeleton */}
        <div style={{
          height: '70px',
          borderBottom: '1px solid #e1e5e9',
          padding: '0 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
            <div style={{
              width: '120px',
              height: '20px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            backgroundColor: '#e0e0e0',
            borderRadius: '50%',
            animation: 'pulse 1.5s ease-in-out infinite'
          }} />
        </div>

        {/* Hero Section Skeleton */}
        <div style={{
          padding: '80px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}>
          <div style={{
            width: '60%',
            maxWidth: '600px',
            height: '48px',
            backgroundColor: '#e0e0e0',
            borderRadius: '8px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }} />
          <div style={{
            width: '40%',
            maxWidth: '400px',
            height: '24px',
            backgroundColor: '#e0e0e0',
            borderRadius: '8px',
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: '0.1s'
          }} />
          <div style={{
            width: '30%',
            maxWidth: '300px',
            height: '20px',
            backgroundColor: '#e0e0e0',
            borderRadius: '8px',
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: '0.2s'
          }} />
        </div>

        {/* Course Cards Skeleton */}
        <div style={{
          padding: '40px',
          display: 'flex',
          gap: '24px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              width: '300px',
              height: '200px',
              backgroundColor: '#e0e0e0',
              borderRadius: '12px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: `${i * 0.1}s`
            }} />
          ))}
        </div>
      </div>
    );
  }

  if (currentView === 'signin') {
    return (
      <div className="App">
        <SignIn
          onClose={navigateToLanding}
          onSwitchToSignUp={navigateToSignUp}
          onSwitchToForgotPassword={navigateToForgotPassword}
          //theme={theme}
          onToggleTheme={toggleTheme}
        />
      </div>
    );
  }

  if (currentView === 'signup') {
    return (
      <div className="App">
        <SignUp
          onClose={navigateToLanding}
          onSwitchToSignIn={navigateToSignIn}
          onNavigateToVerifyEmail={navigateToVerifyEmail}
          //theme={theme}
          onToggleTheme={toggleTheme}
        />
      </div>
    );
  }

  if (currentView === 'forgotpassword') {
    return (
      <div className="App">
        <ForgotPassword
          onClose={navigateToLanding}
          onSwitchToSignIn={navigateToSignIn}
        />
      </div>
    );
  }

  if (currentView === 'resetpassword') {
    return (
      <div className="App">
        <ResetPassword
          onClose={navigateToLanding}
          onSwitchToSignIn={navigateToSignIn}
        />
      </div>
    );
  }

  if (currentView === 'verifyemail') {
    return (
      <div className="App">
        <VerifyEmail
          onNavigateToLanding={navigateToLanding}
          userEmail={currentUser?.email}
        />
      </div>
    );
  }

  if (currentView === 'verified') {
    return (
      <div className="App">
        <Verified
          onNavigateToSignIn={navigateToSignIn}
          onNavigateToLanding={navigateToLanding}
        />
      </div>
    );
  }

  if (currentView === 'authcallback') {
    return (
      <div className="App">
        <AuthCallback />
      </div>
    );
  }

  if (currentView === 'success') {
    return (
      <div className="App">
        <Success
          onNavigateToInquiry={navigateToInquiry}
          onNavigateToLanding={navigateToLanding}
        />
      </div>
    );
  }

  if (currentView === 'coursegallery') {
    // Require authentication for course gallery
    if (!isAuthenticated) {
      return (
        <div className="App">
          <SignIn
            onClose={navigateToLanding}
            onSwitchToSignUp={navigateToSignUp}
            onSwitchToForgotPassword={navigateToForgotPassword}
            //theme={theme}
            onToggleTheme={toggleTheme}
          />
        </div>
      );
    }

    return (
      <div className="App">
        <CourseGallery
          setCurrentView={setCurrentView}
          setCurrentCourseId={(courseId) => {
            setCurrentCourse({ id: courseId });
          }}
        />
      </div>
    );
  }

  if (currentView === 'learn') {
    return (
      <div className="App">
        <Learn
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          currentCourse={currentCourse}
          onCourseSelect={handleCourseSelect}
          onNavigateToInquiry={navigateToInquiry}
          onNavigateToLanding={navigateToLanding}
          //theme={theme}
          onToggleTheme={toggleTheme}
        />
      </div>
    );
  }

  if (currentView === 'termsofuse') {
    return (
      <div className="App">
        <TermsOfUse
          //theme={theme}
          onToggleTheme={toggleTheme}
          onNavigateToLanding={navigateToLanding}
        />
      </div>
    );
  }

  if (currentView === 'privacypolicy') {
    return (
      <div className="App">
        <PrivacyPolicy
          //theme={theme}
          onToggleTheme={toggleTheme}
          onNavigateToLanding={navigateToLanding}
        />
      </div>
    );
  }

  // Determine if user is premium
  const isPremiumUser = isAuthenticated && currentUser?.subscription_type === 'premium';

  // Routing logic:
  // Anonymous/Free users → Landing page with course library
  // Premium users → Inquiry page with course generation

  // Only premium users can access Inquiry.js (formerly Home.js)
  if (currentView === 'inquiry' && isAuthenticated && isPremiumUser) {
    return (
      <div className="App">
        <Inquiry
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          profileLoaded={profileLoaded}
          onSignOut={handleSignOut}
          onNavigateToSignIn={navigateToSignIn}
          onNavigateToSignUp={navigateToSignUp}
          onCourseSelect={handleCourseSelect}
          onNavigateToLearn={navigateToLearn}
          onNavigateToLanding={navigateToLanding}
          onNavigateToTermsOfUse={navigateToTermsOfUse}
          onNavigateToPrivacyPolicy={navigateToPrivacyPolicy}
          currentCourse={currentCourse}
          //theme={theme}
          onToggleTheme={toggleTheme}
          justSignedIn={justSignedIn}
          onClearJustSignedIn={() => setJustSignedIn(false)}
          onRefreshProfile={refreshProfile}
        />
      </div>
    );
  }

  // Anonymous users and free users see Landing page
  return (
    <div className="App">
      <Landing
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        profileLoaded={profileLoaded}
        onNavigateToSignIn={navigateToSignIn}
        onNavigateToSignUp={navigateToSignUp}
        onCourseSelect={handleCourseSelect}
        //theme={theme}
        onToggleTheme={toggleTheme}
        onSignOut={handleSignOut}
        onNavigateToInquiry={navigateToInquiry}
        onNavigateToCourseGallery={navigateToCourseGallery}
        onNavigateToTermsOfUse={navigateToTermsOfUse}
        onNavigateToPrivacyPolicy={navigateToPrivacyPolicy}
        onNavigateToUpgrade={navigateToUpgrade}
      />
    </div>
  );
}

// Wrapper component that provides AuthContext
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
